import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { ChevronDown, ListRestart, X } from 'lucide-react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@herit-fe/heritage-ui';
import { UI_TEXT } from '@/shared/languages';
import type { FilterFieldSurface } from './filter-field';
import { CHIP_LABEL_SEP } from './filter-schema';
import { ScrollArea } from './scroll-area';
import { useIsCompact } from './use-is-compact';

/**
 * 활성 필터 칩 1개. Applied 상태의 투영 — 라벨(+ 편집 진입).
 *
 * **칩에 개별 삭제(x)는 없다.** 조건을 끄는 길은 두 개로 모았다 — 칩을 눌러 그 차원을 중립으로 되돌리거나,
 * 칩 행 끝 [초기화]로 한 번에 되돌리거나. 알약 하나에 「열기」와 「지우기」 두 히트박스를 겹쳐 두면 좁은 화면에서
 * 오조작이 나고, 편집 표면이 이미 중립 선택지(전체·기본값)를 갖고 있어 x 가 하는 일이 그와 겹친다.
 */
export interface FilterChip {
  /** 종류 식별 key(값 충돌 회피용 — 값이 아니라 필드 종류). */
  key: string;
  label: string;
  /**
   * **해제 가능한 조건인가** — 중립(전체·기본값)으로 되돌릴 수 있는 차원이면 true. 칩에 보이는 표식은 없고,
   * 트리거 **배지 집계**(`countRemovableChips`)의 대상만 가른다: 항상 존재하는 필수 차원(예: 조회기간)은
   * false 라 배지에 안 잡힌다("사용자가 건 조건"만 센다).
   */
  removable?: boolean;
  /**
   * **칩 클릭 편집** — 그 차원의 폼 컨트롤(`FilterFieldControl` 의 label+render). `attachFieldControls` 가 주입.
   * pc 에서만 팝오버로 열린다(non-pc 는 전체 조회조건 드로워로 폴백 — `FilterChipsProps.onOpenFilter`).
   */
  control?: { label: string; render: (surface: FilterFieldSurface) => ReactNode };
}

export interface FilterChipsProps {
  chips: FilterChip[];
  /** 목록 aria-label(접근성). */
  label?: string;
  /**
   * non-pc 칩 클릭 폴백 — 전체 조회조건 드로워 열기. 좁은 화면에서 차원마다 미니 오버레이를 또 띄우지 않고
   * 기존 드로워 하나로 모은다(터치 타깃·중첩 깊이). 미주입 시 non-pc 칩은 비대화형.
   */
  onOpenFilter?: () => void;
  /**
   * **편집 표면이 닫힐 때** 호출 — 팝오버가 닫히면 그 안에서 만들어진 임시 상태(Applied 에 남지 않는 UI 값,
   * 예: 알림 기간 프리셋 '직접 선택')를 버린다. 안 버리면 조회는 안 됐는데 다음에 열었을 때 이전 선택이 남는다.
   */
  onDiscard?: () => void;
  /**
   * [초기화](선택) — **칩 행 마지막**에 아이콘 버튼으로 붙는다(ADR ⑧ `ListRestart` 통일). 지우는 대상(칩) 바로
   * 옆에 두어 「무엇이 지워지는지」를 눈으로 잇는다. 변경 있을 때만(호출처 `isDirty`) 주입.
   */
  onReset?: () => void;
  className?: string;
}

/**
 * 칩 알약(`li`) 룩 — 배경·모양·타이포만. **안쪽 패딩은 알약이 갖지 않는다** — 클릭 대상이 칩 전면이어야 하므로
 * 패딩을 트리거(라벨) 쪽으로 넘겨 라벨 요소가 알약을 가득 채우게 한다(패딩 여백을 눌러도 열림).
 * 말줄임 전제인 `min-w-0`(flex item 기본 min-width:auto 해제)은 알약·라벨 양쪽에 필요.
 *
 * **치수는 브레이크포인트 불변, 글자 크기만 반응형**(`text-xs lg:text-sm`) — 알약·x·초기화 아이콘이 pc 에서
 * 같이 커지면 칩 행 높이가 화면 폭 따라 널뛰어(24↔28px) 아래 콘텐츠 시작선이 밀린다. 높이는 라벨의
 * `leading-7` 이 홀로 정하므로(아래) 글자만 갈아 끼워도 상자는 그대로다.
 */
const CHIP_PILL =
  'relative inline-flex min-w-0 max-w-full shrink-0 items-center rounded-full bg-background text-xs text-foreground shadow-sm lg:text-sm dark:bg-gray-800';

/**
 * 라벨 영역 = **알약 전체를 채우는** 패딩(`w-full`) — x 가 흐름에서 빠져 떠 있으므로(아래) 라벨이 알약 폭을 다 갖는다.
 * x 가 있을 때 우측 패딩만 그 몫으로 넓혀 글자가 x 밑으로 들어가지 않게 한다(자리 확보는 패딩, 클릭 영역은 전면).
 * 패딩은 **좌우 대칭 `2.5` 고정** — 브레이크포인트로 갈리는 값이 하나도 없다.
 *
 * ⚠️ **세로 패딩(`py-*`) 대신 `leading-7`(28px) 로 높이를 잡는다** — 이게 「칩 크기 불변, 글자만 반응형」의 핵심.
 * `py` 방식은 높이 = 패딩 + **글꼴의 line-height** 라 `text-xs`(16) → `lg:text-sm`(20) 만으로 상자가 4px 자란다.
 * 줄높이를 상수로 박으면 line box 가 글자를 세로 중앙에 앉히면서 높이는 글꼴과 무관하게 고정된다.
 * (Tailwind v4 의 `text-*` 는 line-height 를 `var(--tw-leading, …)` 로 참조하므로, 명시 `leading-*` 이
 * 선언 순서와 무관하게 이긴다.) 라벨 높이 = 알약 높이라 알약 전면이 그대로 클릭 영역으로 남는다.
 *
 * ⚠️ 28px(`leading-7`)은 **초기화 버튼 `size-7` 과 같은 값이어야** 한다(칩 행 교차축). 상수로 빼서 조합하면
 * Tailwind 문자열 스캐너가 후보를 못 찾아 CSS 가 생성되지 않으므로 양쪽 다 리터럴로 적는다.
 */
const CHIP_LABEL = 'w-full min-w-0 truncate rounded-full leading-7 px-2.5';

/**
 * **눌리는** 칩(= `control` 이 실려 팝오버·드로워를 여는 칩)의 라벨. 비대화형 칩은 위 `chipLabelClass` 를 쓴다.
 *
 * 정적 어포던스가 필요해서 갈라졌다 — 종전 신호는 `cursor-pointer` 와 `hover:` 뿐이라 **둘 다 마우스 전용**이었고,
 * 모바일에선 「눌러서 고칠 수 있다」를 알릴 방법이 하나도 없었다(x 는 "지울 수 있는 태그" 신호지 편집 신호가 아니다).
 * 그래서 트레일링 캐럿(`ChevronDown`)을 붙인다 — select 관습이라 학습 비용이 없고 hover 없이도 보인다.
 *
 * ⚠️ 블록 대신 **flex** 인 이유: 캐럿이 말줄임에 먹히면 안 된다. `truncate` 는 텍스트 span 으로 내리고
 * (flex item 은 블록화돼 말줄임이 산다) 캐럿은 `shrink-0` 으로 흐름에서 고정한다.
 * 높이는 그대로 `leading-7` 이 정한다 — 컨테이너에 걸면 자식 span 으로 **상속**돼 flex item 높이 = 28px 이 된다.
 * 우측 패딩(`pr-7`)은 여전히 x 자리라, 캐럿은 그 **안쪽**에 앉는다(캐럿 → x 순서).
 */
const CHIP_TRIGGER =
  'flex w-full min-w-0 cursor-pointer items-center gap-1 rounded-full px-2.5 text-left leading-7 hover:bg-foreground/5';

/**
 * 칩 행 컨테이너 — **compact(<lg) 는 한 줄 가로 스크롤 / pc 는 종전 줄바꿈**.
 *
 * 좁은 화면에서 칩이 개행되면 행이 2~3줄로 자라 아래 콘텐츠(표·차트)를 그만큼 밀어낸다 — 조회조건은 보조 정보라
 * 세로를 먹으면 안 된다. 그래서 compact 는 `flex-nowrap` + `overflow-x-auto` 로 옆으로 흘린다.
 * 스크롤바는 숨긴다(Tailwind 기본 유틸 없음 → 표준 `scrollbar-width` + WebKit 의사요소 둘 다 필요) — 얇은 행에
 * 가로 스크롤바가 뜨면 칩 높이만큼의 띠를 또 차지해 개행을 피한 의미가 없어진다. 스크롤 가능 여부는 잘린 칩이
 * 알려준다. pc 는 폭이 넉넉하고 칩 팝오버가 열려야 하므로(클리핑 금지) `overflow-visible` + 줄바꿈으로 되돌린다.
 *
 * ⚠️ 짝: `CHIP_PILL` 의 `shrink-0` — nowrap 컨테이너에서 이게 없으면 칩들이 폭에 맞춰 압축돼 스크롤이 아니라
 * 전부 짜부라진다(`max-w-full` 이 여전히 상한이라 pc 말줄임 동작은 그대로).
 *
 * ⚠️ `ScrollArea`(조합 스크롤 만국규칙, ADR ⑨) 를 쓰지 않는 유일한 이유 = **스크롤바를 안 보이게** 해야 해서다.
 * 그 래퍼는 오버레이 스크롤바를 항상 그리고, 양축 스크롤 컨테이너라 한 줄짜리 칩 행에는 과하다.
 *
 * ⚠️ `py-1 -my-1` = **칩 그림자(`shadow-sm`) 살리기**. `overflow-x` 를 `visible` 이 아닌 값으로 두면 CSS 명세상
 * **반대축도 `visible` 로 남지 못하고 `auto` 로 강제**돼(overflow 축 상호 의존), 세로로 삐져나가는 그림자가
 * 스크롤포트 위아래 경계에서 잘린다. 패딩으로 그림자 몫(아래 4px·위 2px)을 스크롤포트 안에 확보하고
 * 같은 크기의 음수 마진으로 되돌려 **행 높이는 종전 그대로** 둔다(레이아웃 불변, 그림자만 회복).
 * pc 는 `overflow-visible` 이라 애초에 잘릴 일이 없어 `lg:` 에서 둘 다 해제한다.
 */
const CHIP_LIST =
  'flex flex-nowrap gap-1.5 overflow-x-auto py-1 -my-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:my-0 lg:flex-wrap lg:overflow-visible lg:py-0';

/**
 * 스크롤 가장자리 페이드 — **그쪽에 더 볼 게 남았다**는 어포던스. 오버레이 div 가 아니라 컨테이너 자체의
 * `mask-image` 다. 오버레이는 뒤 배경색을 알아야 하는데(`from-<bg>` → `to-transparent`) 칩 행이 놓이는 면은
 * 페이지·카드·시트로 제각각이라 색을 하나로 못 박는다. 마스크는 **알파를 깎는 방식**이라 배경 무지 —
 * 어느 면 위에서도 맞는다. 페이드 폭은 양끝 `2rem`.
 *
 * ⚠️ **네 상태를 각각 완성된 리터럴로** 적는다(조합 금지) — Tailwind 는 소스를 문자열 스캔해 클래스를 만들므로
 * 조각을 이어 붙이면 후보를 못 찾아 CSS 가 생성되지 않는다.
 */
const CHIP_FADE_START = '[mask-image:linear-gradient(to_right,transparent,#000_2rem)]';
const CHIP_FADE_END = '[mask-image:linear-gradient(to_right,#000_calc(100%-2rem),transparent)]';
const CHIP_FADE_BOTH =
  '[mask-image:linear-gradient(to_right,transparent,#000_2rem,#000_calc(100%-2rem),transparent)]';

/** 닿은 쪽은 페이드를 끈다 — 양끝 다 닿았으면(=안 넘침) 마스크 자체가 없다. */
function chipFadeClass(atStart: boolean, atEnd: boolean): string {
  if (atStart && atEnd) return '';
  if (atStart) return CHIP_FADE_END;
  if (atEnd) return CHIP_FADE_START;
  return CHIP_FADE_BOTH;
}

/**
 * 가로 스크롤이 **양끝에 닿았는지** — 페이드 표시 조건.
 *
 * 끝(오른쪽)은 **반드시 꺼야 한다**: 칩 흐름의 마지막 항목이 [초기화] **버튼**이라, 상시 마스크였다면 끝까지 민
 * 순간 그 버튼이 반투명해져 비활성처럼 보인다. 시작(왼쪽)은 대칭을 위한 것 — 첫 칩만 걸리므로 위험은 없다.
 * (내용이 안 넘치면 양끝 동시 true 라 마스크 없음. pc 는 `overflow-visible` + 줄바꿈이라 넘칠 일이 없어
 * 브레이크포인트 분기 없이 자동으로 꺼진다.)
 *
 * 구독은 마운트 1회(스크롤 + 컨테이너 리사이즈), 측정은 **매 렌더**(칩이 늘고 줄면 `scrollWidth` 가 변하는데
 * `ResizeObserver` 는 컨테이너 박스만 보므로 그 변화를 못 잡는다).
 *
 * ⚠️ 두 값을 **객체 한 덩이로 묶지 않는다** — 매 렌더 측정이라 매번 새 객체가 나오면 React 가 변경으로 보고
 * 리렌더 → 다시 측정 → … 무한 루프가 된다. 원시값 state 로 나눠 두면 같은 값일 때 React 가 알아서 멈춘다.
 */
function useScrollEdges(ref: RefObject<HTMLElement | null>): { atStart: boolean; atEnd: boolean } {
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // 1px 여유 — 확대·소수 픽셀 폭에서 scrollLeft 합이 scrollWidth 에 정확히 안 닿는다.
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, [ref]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.addEventListener('scroll', measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [ref, measure]);
  useLayoutEffect(measure);
  return { atStart, atEnd };
}

/**
 * 팝오버가 열릴 때 포커스를 줄 **본문 첫 컨트롤** 셀렉터.
 *
 * base-ui 기본값(팝업 안 첫 tabbable)에 맡기지 않는 이유 — 내용이 넘치면 `ScrollArea` 뷰포트가 `tabIndex=0`
 * 을 받아(base-ui `ScrollAreaViewport`) **그게 첫 tabbable 이 된다**. 그러면 짧은 팝오버는 입력칸으로,
 * 긴 팝오버는 스크롤 컨테이너로 포커스가 갈려 같은 조작이 화면마다 다르게 동작한다.
 */
const POPOVER_FIRST_CONTROL =
  'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled])';

/**
 * 칩에 표시할 **값**의 최대 길이(글자). 차원명은 대상 아님 — 자르는 건 뒤쪽 값뿐.
 *
 * 하한은 **기간 값 한 벌**(`yyyy-MM-dd ~ yyyy-MM-dd` = 23자)이 온전히 보이는 길이다. 이보다 짧게 잡으면 조회기간
 * 칩의 **종료일이 통째로 사라져** 칩만 보고는 어느 구간을 보고 있는지 알 수 없다(가장 자주 보는 차원인데도).
 * 이 상한이 실제로 자르는 건 인버터 이름 나열처럼 길이가 열려 있는 값들이다.
 */
const CHIP_VALUE_MAX = 23;

/**
 * 표시 조각 — 칩 라벨 `차원: 값` 을 **차원명(muted) / 값(기본 톤)** 두 조각으로 나눈다. 한 칩 안에서 무엇이
 * 「이름」이고 무엇이 「지금 값」인지 색으로 갈라, 칩이 여럿 늘어서도 값만 훑을 수 있게 한다.
 *
 * 값은 상한(`CHIP_VALUE_MAX`)으로 줄인다 — 값 하나가 길면(인버터 이름 나열·기간 범위) 그 칩이 행을 독식하고
 * 나머지가 다음 줄로 밀린다. CSS `truncate` 는 **남는 폭**에만 반응해 폭이 넉넉하면 안 줄어들므로, 폭과 무관한
 * 글자수 상한을 함께 건다.
 *
 * ⚠️ **원본 `chip.label` 은 건드리지 않는다**(표시 전용) — 엑셀 내보내기 조회조건·a11y·title 은 온전한 값을 쓴다.
 * 구분자가 없는 칩(차원명 없이 값만)은 전체를 값으로 본다.
 */
function chipParts(label: string): { head: string; value: string; clipped: boolean } {
  const sep = label.indexOf(CHIP_LABEL_SEP);
  const head = sep < 0 ? '' : label.slice(0, sep + CHIP_LABEL_SEP.length);
  const raw = sep < 0 ? label : label.slice(sep + CHIP_LABEL_SEP.length);
  const clipped = raw.length > CHIP_VALUE_MAX;
  return { head, value: clipped ? `${raw.slice(0, CHIP_VALUE_MAX)}…` : raw, clipped };
}

/**
 * 활성 필터 칩 행 — **전 breakpoint 노출**(필터 셸 규칙). 도메인 무지: 칩 데이터·제거 정책은 호출처가 주입.
 * 칩이 0개면 렌더하지 않는다(트리거 행만 노출).
 *
 * 칩은 **읽기 전용 투영 + 편집 진입점** 두 역할을 겸한다 — `control` 이 실린 칩은 pc 에서 그 차원만 팝오버로
 * 열고(드로워 왕복 없이 한 번에 고치기), non-pc 는 `onOpenFilter` 로 기존 드로워를 연다.
 */
export function FilterChips({
  chips,
  label,
  onOpenFilter,
  onDiscard,
  onReset,
  className,
}: FilterChipsProps) {
  const isCompact = useIsCompact();
  const listRef = useRef<HTMLUListElement>(null);
  const { atStart, atEnd } = useScrollEdges(listRef);
  if (chips.length === 0) return null;
  return (
    <ul
      ref={listRef}
      aria-label={label ?? UI_TEXT.FILTER_ACTIVE}
      className={`${className ?? CHIP_LIST} ${chipFadeClass(atStart, atEnd)}`}
    >
      {chips.map((chip) => (
        <ChipItem
          key={chip.key}
          chip={chip}
          isCompact={isCompact}
          onOpenFilter={onOpenFilter}
          onDiscard={onDiscard}
        />
      ))}
      {/* 초기화 — 칩 흐름의 마지막 항목(줄바꿈도 칩과 함께 탄다).
          ⚠️ 높이는 **칩 알약과 정확히 같아야** 한다(`size-7` = 알약의 `leading-7` = 28px, 전 breakpoint 동일).
          더 크면 이 버튼이 행의 교차축 크기를 정해 칩들이 stretch 되고, 칩 행 전체에 상하 여백이 생긴 것처럼 보인다. */}
      {onReset ? (
        <li className="flex shrink-0 items-center">
          {/* 아이콘만 있는 버튼이라 툴팁으로 뜻·범위를 보충한다(사이드바 토글과 같은 패턴).
              `TooltipTrigger render` 로 버튼을 **대체 렌더** — 감싸면 button in button 이 된다.
              칩 행은 화면 상단이 아니라 아래로 펴도 잘리지 않으므로 기본 방향(top) 유지. */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={UI_TEXT.FILTER_RESET}
                    onClick={onReset}
                    className="size-7 rounded-full text-muted-foreground hover:text-foreground"
                  >
                    <ListRestart className="size-4" />
                  </Button>
                }
              />
              <TooltipContent>{UI_TEXT.FILTER_RESET_HINT}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </li>
      ) : null}
    </ul>
  );
}

/**
 * 칩 하나 — 표면 분기 한 곳. pc + `control` = **칩 알약 자체가 팝오버 트리거**(그 차원만 편집),
 * non-pc + `control` = 전체 드로워 폴백, 컨트롤이 없으면 종전 그대로 비대화형 텍스트.
 * 팝오버 본문은 **드로워 스택과 같은 `render()`** 라 컨트롤 정의가 중복되지 않는다.
 *
 * x 는 트리거 **바깥**의 형제다 — 버튼 중첩(무효 HTML)을 피하고, 제거 클릭이 편집 오버레이를 함께 여는 것도 막는다.
 */
function ChipItem({
  chip,
  isCompact,
  onOpenFilter,
  onDiscard,
}: {
  chip: FilterChip;
  isCompact: boolean;
  onOpenFilter?: () => void;
  onDiscard?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  // 닫힘은 한 곳으로 모은다(바깥클릭·Esc·닫기 버튼·컨트롤의 close) — 임시 상태 폐기를 한 경로에서만 부르게.
  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (!next) onDiscard?.();
  };
  // 표시는 줄이고 전체 값은 title(호버)로 남긴다 — 줄지 않았으면 title 도 생략(중복 툴팁 노이즈 방지).
  const { head, value, clipped } = chipParts(chip.label);
  const title = clipped ? chip.label : undefined;
  // 차원명은 muted, 값은 기본 톤 — 세 표면(팝오버 트리거·드로워 폴백 버튼·비대화형 span)이 같은 조각을 쓴다.
  // 구분자가 없으면(= 적을 값이 없는 차원, 예: 빈 검색어) 통째로 muted — "여긴 조건이 안 걸렸다"가 형태로 읽힌다.
  const text = head ? (
    <>
      <span className="text-muted-foreground">{head}</span>
      {value}
    </>
  ) : (
    <span className="text-muted-foreground">{value}</span>
  );

  // 말줄임은 텍스트 조각이 진다(캐럿이 붙는 pc 는 flex 라 컨테이너가 못 자른다).
  const labelBody = <span className="min-w-0 truncate">{text}</span>;

  if (chip.control && !isCompact) {
    return (
      <li className={CHIP_PILL}>
        <Popover open={open} onOpenChange={changeOpen}>
          <PopoverTrigger
            className={CHIP_TRIGGER}
            // a11y 이름엔 **온전한** 값을 싣는다(화면은 줄여 보이더라도).
            aria-label={UI_TEXT.FILTER_CHIP_EDIT(chip.label)}
            title={title}
          >
            {labelBody}
            {/* 캐럿은 **pc 전용**(이 분기 자체가 `!isCompact`) — 「눌러서 고칠 수 있다」를 hover·cursor 없이
                알리는 정적 신호다. compact 는 칩 행이 한 줄 가로 스크롤이라 칩마다 아이콘을 더하면 폭만 먹고,
                터치에선 알약 전체가 곧 타깃이라 화살표가 가리킬 대상이 따로 없다. */}
            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground/70" />
          </PopoverTrigger>
          {/* 한 차원짜리 폼 — 필드 간격은 드로워 스택(gap-5)과 맞춘다. 면은 카드(팝오버 기본 표면).
              ⚠️ 팝오버가 **높이 상한 + flex column** 이어야 안쪽 `ScrollArea` 가 산다 — 부모 높이가 auto 면
              뷰포트의 `h-full` 이 auto 로 풀려 내용만 길어지고 스크롤이 안 걸린다(드로워 본문과 같은 레시피).
              상한 = base-ui Positioner 가 주는 `--available-height`(앵커 기준 남는 공간)와 60vh 중 작은 값. */}
          <PopoverContent
            align="start"
            aria-label={chip.control.label}
            // 열리면 본문 첫 컨트롤로 포커스(닫기 X 도, 스크롤 뷰포트도 아닌). 터치로 열렸을 땐 기본 동작에
            // 맡긴다 — base-ui 가 가상 키보드를 피하려고 팝업 자체를 포커스한다.
            initialFocus={(openType) =>
              openType === 'touch'
                ? true
                : (bodyRef.current?.querySelector<HTMLElement>(POPOVER_FIRST_CONTROL) ?? true)
            }
            className="relative flex max-h-[min(60vh,var(--available-height,60vh))] flex-col bg-card p-0"
          >
            {/* 긴 컨트롤(인버터 스위치 목록 등)은 팝오버를 넘길 수 있어 `ScrollArea` 로 흡수(ADR ⑨).
                `flex-1 min-h-0` 이 상한 안에서 남는 높이를 확정해 준다 — 이게 없으면 뷰포트가 안 잘린다.
                `pr-6` = 닫기(X) 자리 확보(첫 필드 라벨이 X 밑으로 들어가지 않게). */}
            <ScrollArea className="flex min-h-0 flex-1 flex-col" scrollbarInsetY>
              {/* 컨트롤에 **닫기 능력**을 넘긴다 — 「고르면 끝」인 컨트롤만 스스로 닫는다(복수 선택·복수 필드는 유지). */}
              <div ref={bodyRef} className="flex flex-col gap-5 p-4">
                {chip.control.render({ close: () => changeOpen(false), live: true })}
              </div>
            </ScrollArea>
            {/* 닫기(X) — **DOM 상 본문 뒤**(탭 순서 「내용 먼저 → 닫기」). 위치는 absolute 라 그대로 우상단.
                본문 앞에 있던 탓에 팝오버를 열면 X 가 첫 tabbable 이라 포커스를 먹었다. — pc 드로워(`DrawerCloseButton`)와 같은 우상단 관행. 바깥클릭·Esc 외의 명시적 출구. */}
            <Button
              variant="ghost"
              size="icon"
              aria-label={UI_TEXT.COMMON_CLOSE}
              onClick={() => changeOpen(false)}
              className="absolute right-2 top-2 z-10 size-7 text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" />
            </Button>
          </PopoverContent>
        </Popover>
      </li>
    );
  }

  if (chip.control && onOpenFilter) {
    return (
      <li className={CHIP_PILL}>
        <button
          type="button"
          className={CHIP_TRIGGER}
          onClick={onOpenFilter}
          aria-label={UI_TEXT.FILTER_CHIP_EDIT(chip.label)}
          title={title}
        >
          {labelBody}
        </button>
      </li>
    );
  }

  return (
    <li className={CHIP_PILL}>
      <span className={CHIP_LABEL} title={title}>
        {text}
      </span>
    </li>
  );
}
